import {NgClass} from '@angular/common';
import {Component, EventEmitter, Input, Output} from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  AmountPipe,
  dateToString,
  EmDatePipe,
  EmTranslatePipe,
  FilterDropdownOptionsPipe,
  stringToDate,
  TranslationService,
  validators,
} from '@emma-labs/shared/angular';
import {
  CurrentProtection,
  Insurer,
  REPLACE_POLICY_OPTIONS,
  ReplaceInsurance,
} from '@emma-labs/shared/typescript';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {NgxMaskDirective} from 'ngx-mask';
import {ButtonModule} from 'primeng/button';
import {DialogModule} from 'primeng/dialog';
import {DividerModule} from 'primeng/divider';
import {DropdownModule} from 'primeng/dropdown';
import {InputTextModule} from 'primeng/inputtext';
import {RadioButtonModule} from 'primeng/radiobutton';
import {TableModule} from 'primeng/table';
import {debounceTime, distinctUntilChanged} from 'rxjs';
import {DEBOUNCE_TIME} from '../../../../utils/utility-constants';

interface OtherProductForm {
  coverageAmount: FormControl<number | null>;
  coverageType: FormControl<string | null>;
  issueDate: FormControl<string | null>;
  issuerName: FormControl<string | null>;
  isReplacing: FormControl<ReplaceInsurance | null>;
}

interface EmmaProductForm {
  policyNumber: FormControl<string | null>;
  isReplacing: FormControl<ReplaceInsurance | null>;
  coverageAmount: FormControl<number | null>;
  issueDate: FormControl<string | null>;
  issuerName: FormControl<Insurer | null>;
}

@UntilDestroy()
@Component({
  selector: 'em-current-protections-section',
  templateUrl: './current-protections-section.component.html',
  styleUrls: ['./current-protections-section.component.scss'],
  imports: [
    TableModule,
    ButtonModule,
    DialogModule,
    ReactiveFormsModule,
    NgClass,
    InputTextModule,
    RadioButtonModule,
    FormsModule,
    DividerModule,
    DropdownModule,
    FilterDropdownOptionsPipe,
    NgxMaskDirective,
    EmTranslatePipe,
    AmountPipe,
    EmDatePipe,
  ],
})
export class CurrentProtectionsSectionComponent {
  @Input() isReadOnly = false;
  @Input() currentProtections: CurrentProtection[] = [];

  @Input() set protectionSearchResult(value: CurrentProtection | undefined) {
    if (value) {
      this.emmaProductForm.patchValue({
        coverageAmount: value.coverageAmount,
        issueDate: dateToString(value.issueDate),
      });

      this.currentProtectionFound = true;
    }
  }

  @Output() formSubmitted = new EventEmitter<CurrentProtection[]>();
  @Output() policyNumberChanged = new EventEmitter<{
    output: string;
    callback?: (status: boolean) => void;
  }>();

  showDialog = false;
  otherProductForm: FormGroup<OtherProductForm>;
  emmaProductForm: FormGroup<EmmaProductForm>;
  isEmmaProduct = false;
  currentProtectionFound = false;
  isButtonLoading = false;
  readonly replacePolicyOptions = REPLACE_POLICY_OPTIONS;
  readonly replaceInsurance = ReplaceInsurance;
  readonly isFr: boolean;
  private modifyIndex = -1;

  constructor(
    readonly translate: TranslationService,
    private readonly fb: FormBuilder,
  ) {
    this.isFr = this.translate.currentLanguageIsFr();

    this.otherProductForm = this.createOtherProductForm();
    this.emmaProductForm = this.createEmmaProductForm();
  }

  confirm(): void {
    this.isButtonLoading = true;

    if (this.modifyIndex >= 0) {
      this.currentProtections[this.modifyIndex] = this.formToCurrentProtection();
    } else {
      this.currentProtections.push(this.formToCurrentProtection());
    }
    this.formSubmitted.emit(this.currentProtections);
    this.showDialog = false;
    this.resetForms();
  }

  displayDialog(): void {
    this.showDialog = true;
  }

  modifyProtection(index: number): void {
    this.modifyIndex = index;
    this.prefillForm(this.currentProtections[index]);
    this.showDialog = true;
  }

  deleteProtection(index: number): void {
    this.currentProtections.splice(index, 1);
    this.formSubmitted.emit(this.currentProtections);
    this.resetForms();
  }

  resetForms(): void {
    this.otherProductForm.reset(undefined);
    this.emmaProductForm.reset(undefined);
    this.emmaProductForm.controls.issuerName.setValue(Insurer.HUMANIA);
    this.currentProtectionFound = false;
    this.modifyIndex = -1;
  }

  private prefillForm(currentProtection: CurrentProtection): void {
    if (currentProtection.issuerName === Insurer.HUMANIA) {
      this.isEmmaProduct = true;
      this.emmaProductForm.patchValue(
        {
          policyNumber: currentProtection.policyNumber,
          isReplacing: currentProtection.isReplacing,
          coverageAmount: currentProtection.coverageAmount,
          issueDate: dateToString(currentProtection.issueDate),
        },
        {emitEvent: false},
      );
      return;
    }

    this.otherProductForm.patchValue({
      ...currentProtection,
      issueDate: currentProtection.issueDate ? dateToString(currentProtection.issueDate) : null,
      isReplacing: currentProtection.isReplacing,
    });
  }

  private formToCurrentProtection(): CurrentProtection {
    const form = this.isEmmaProduct ? this.emmaProductForm : this.otherProductForm;
    const issueDate = form.getRawValue().issueDate;

    const protection: CurrentProtection = {
      ...form.getRawValue(),
      issueDate: issueDate ? stringToDate(issueDate) : undefined,
    } as CurrentProtection;

    return protection;
  }

  private createOtherProductForm(): FormGroup<OtherProductForm> {
    return this.fb.group<OtherProductForm>({
      coverageAmount: this.fb.control(null, [Validators.required, validators.isPositive]),
      coverageType: this.fb.control(null),
      issueDate: this.fb.control(null, [validators.date]),
      issuerName: this.fb.control(null),
      isReplacing: this.fb.control(null, [Validators.required]),
    });
  }

  private createEmmaProductForm(): FormGroup<EmmaProductForm> {
    const form = this.fb.group<EmmaProductForm>({
      policyNumber: this.fb.control(null, {validators: [Validators.required]}),
      isReplacing: this.fb.control(null, [Validators.required]),
      coverageAmount: this.fb.control({value: null, disabled: true}),
      issueDate: this.fb.control({value: null, disabled: true}),
      issuerName: this.fb.control({value: Insurer.HUMANIA, disabled: true}),
    });

    form.controls.policyNumber.valueChanges
      .pipe(debounceTime(DEBOUNCE_TIME), untilDestroyed(this), distinctUntilChanged())
      .subscribe((policyNumber) => {
        this.currentProtectionFound = false;

        if (!policyNumber) {
          return;
        }

        this.policyNumberChanged.emit({
          output: policyNumber,
          callback: (status: boolean) => {
            if (!status) {
              form.controls.isReplacing.reset(undefined, {emitEvent: false});
              form.controls.coverageAmount.reset(undefined, {emitEvent: false});
              form.controls.issueDate.reset(undefined, {emitEvent: false});
            }
          },
        });
      });

    return form;
  }
}
